import React, { useMemo, useRef } from "react";
import { Home } from "lucide-react";
import FormattedNumberInput from "../FormattedNumberInput";
import useGooglePlacesAutocomplete from "../../hooks/useGooglePlacesAutocomplete";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  SCHL_BENCHMARK_VERSIONS,
  resolveSchlBenchmarkVersion,
} from "../../defaults/schlBenchmarks";
import { parseGooglePlaceAddress } from "../../utils/parseGooglePlaceAddress";
import { getBuildingImageGallery } from "../../utils/propertyImages";
import SectionShell from "./SectionShell";

export default function BasicInfo({
  property = {},
  onChange,
  advancedExpenses,
  readOnly = false,
  disablePlaceAutocomplete = false,
  purchasePriceLabel,
  isOwned = false,
  collapsible = false,
  defaultCollapsed = true,
}) {
  const { t } = useLanguage();
  const purchaseLabel =
    purchasePriceLabel ||
    (isOwned ? t("basicInfo.estimatedMarketValue") : t("basicInfo.purchasePrice"));
  const handleChange = (field, value) => {
    onChange({ ...property, [field]: value });
  };

  const autocompleteOptions = useMemo(
    () => ({
      componentRestrictions: { country: "ca" },
      fields: ["address_components"],
    }),
    []
  );

  const addressRef = disablePlaceAutocomplete
    ? useRef(null)
    : useGooglePlacesAutocomplete(
        (place) => {
          const parsed = parseGooglePlaceAddress(place);
          onChange({
            ...property,
            address: parsed.street,
            city: parsed.city,
            province: parsed.province,
            postalCode: parsed.postalCode,
          });
        },
        autocompleteOptions
      );

  return (
    <SectionShell
      title={t("basicInfo.title")}
      icon={Home}
      titleClassName="text-blue-600"
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      <div>
        <label className="block text-sm font-medium mb-1">{t("basicInfo.address")}</label>
        <input
          ref={addressRef}
          type="text"
          value={property.address || ""}
          onChange={(e) => handleChange("address", e.target.value)}
          className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
          placeholder="123 Example St"
          disabled={readOnly}
          readOnly={readOnly}
          required
        />
      </div>
      <div className="grid grid-cols-1 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.city")}</label>
          <input
            type="text"
            value={property.city || ""}
            onChange={(e) => handleChange("city", e.target.value)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            placeholder="Montreal"
            disabled={readOnly}
            readOnly={readOnly}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.province")}</label>
          <input
            type="text"
            value={property.province || ""}
            onChange={(e) => handleChange("province", e.target.value)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            placeholder="QC"
            disabled={readOnly}
            readOnly={readOnly}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.postalCode")}</label>
          <input
            type="text"
            value={property.postalCode || ""}
            onChange={(e) => handleChange("postalCode", e.target.value)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            placeholder="H2B 1A0"
            disabled={readOnly}
            readOnly={readOnly}
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 mt-4">
        {advancedExpenses && !isOwned && (
          <div>
            <label className="block text-sm font-medium mb-1">{t("basicInfo.askingPrice")}</label>
            <FormattedNumberInput
              value={property.askingPrice || ""}
              onChange={(val) => handleChange("askingPrice", val)}
              className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
              placeholder="0"
              type="currency"
              disabled={readOnly}
              readOnly={readOnly}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">{purchaseLabel}</label>
          <FormattedNumberInput
            value={property.purchasePrice || ""}
            onChange={(val) => handleChange("purchasePrice", val)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            placeholder="0"
            type="currency"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>
        {advancedExpenses && (
          <div>
            <label className="block text-sm font-medium mb-1">{t("basicInfo.municipalEvaluation")}</label>
            <FormattedNumberInput
              value={property.municipalEvaluation || ""}
              onChange={(val) => handleChange("municipalEvaluation", val)}
              className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
              placeholder="0"
              type="currency"
              disabled={readOnly}
              readOnly={readOnly}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.numberOfUnits")}</label>
          <FormattedNumberInput
            value={property.numberOfUnits || ""}
            onChange={(val) => handleChange("numberOfUnits", val)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            placeholder="0"
            disabled={readOnly}
            readOnly={readOnly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.structureType")}</label>
          <select
            value={property.structureType || 'woodFrame'}
            onChange={(e) => handleChange('structureType', e.target.value)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            disabled={readOnly}
          >
            <option value="woodFrame">{t("basicInfo.structureType.woodFrame")}</option>
            <option value="concrete">{t("basicInfo.structureType.concrete")}</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">{t("basicInfo.schlBenchmarkVersion")}</label>
          <select
            value={resolveSchlBenchmarkVersion(property.schlBenchmarkVersion)}
            onChange={(e) => handleChange('schlBenchmarkVersion', e.target.value)}
            className={`w-full border rounded p-2 ${readOnly ? 'bg-gray-100' : ''}`}
            disabled={readOnly}
          >
            {SCHL_BENCHMARK_VERSIONS.map((version) => (
              <option key={version} value={version}>
                {t(`basicInfo.schlBenchmarkVersion.${version}`)}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{t("basicInfo.schlBenchmarkVersion.hint")}</p>
        </div>
      </div>

      {readOnly && getBuildingImageGallery(property).length > 0 ? (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <span className="block text-sm font-medium mb-2">{t("basicInfo.buildingPhotoLabel")}</span>
          <div className="flex flex-wrap gap-2">
            {getBuildingImageGallery(property).map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                className="max-h-36 rounded-lg border border-gray-200 object-cover"
              />
            ))}
          </div>
        </div>
      ) : null}
    </SectionShell>
  );
}
